<template>
  <div class="row col-xl-12 mx-0 px-0">
     <!--start::TOPFORM-->
    <Topform
      ref="admin-users-topform"
      :managementType="managementType"
      :status="calculatedStatus"
      pageType="system_admin"
      :name="userInfo.name"
      :errors="userErrors"
      :submissionStates="submissionStates"
      parentPage="/users/view"
      :tabs="computedTabs"
      :validation="computedvalidation"
      :loadingComplete="loadingComplete"
      @clearErrors="clearErrors"
      @asset_reservation_created="form.avatar = $event"
      @asset_reservation_deleted="form.avatar = null"
      @remove_project_asset="form.avatar_remove = $event"
      @submit="submit"
      @clear_existing_data="clearData"
    >
    </Topform>
    <!--end::TOPFORM-->
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { validationMixin } from "vuelidate";
import { maxLength, minLength, required, email } from "vuelidate/lib/validators";
import { SET_BREADCRUMB } from "@/core/services/store/system/breadcrumbs.module";
import PageLoader from "@/core/classes/PageLoader.js";
import Toast from "@/core/classes/Toast.js";

//Vuex Imports
import {
  POPULATE_SYSTEM_ADMINISTRATOR_INFO,
  CREATE_SYS_ADMINISTRATOR,
  UPDATE_SYS_ADMINISTRATOR,
  CLEAR_USER_ERRORS,
  CLEAR_USER_INFO
} from "@/core/services/store/core/user.module";

//Topform Imports
import TopformConfig from "@/core/config/topform/ManageAdminUser.js";

export default {
  mixins: [validationMixin],

  name: "ManageSysAdmin",

  data() {
    return {
      pageLoader: new PageLoader(0),
      toast: new Toast(),
      submissionStates: {
        submitting: false
      },
      error: {
        active: false,
        message: ""
      },
      form: {
        name: "",
        display_name: "",
        email: "",
        suspended: 0,
        password: "",
        password_confirmation: "",
        system_country_code: "GBR",
        system_language_code: "eng",
        system_timezone_code: "UTC",
        system_admin_role_id: "1",
        avatar: null,
        avatar_remove: null
      },
      initialAvatar: null
    }
  },

  validations: {
    form: {
      name: {
        required,
        maxLength: maxLength(255)
      },
      display_name: {
        required,
        maxLength: maxLength(255),
        isValid: (value, vm) => {
          let spaceLessValue = value.replace(/\s/g, '').toLowerCase();
          let spaceLessName = vm.name.replace(/\s/g, '').toLowerCase();
          let check1 = !spaceLessValue.includes(spaceLessName)
          let check2 = !spaceLessName.includes(spaceLessValue)
          return check1 && check2;
        }
      },
      email: {
        required,
        maxLength: maxLength(255),
        email
      },
      suspended: {},
      system_country_code: {
        required
      },
      system_language_code: {
        required
      },
      system_timezone_code: {
        required
      },
      system_admin_role_id: {
        required
      },
      avatar: {},
      avatar_remove: {},
      password: {
        minLength: minLength(8),
        maxLength: maxLength(24)
      },
      password_confirmation: {
        minLength: minLength(8),
        maxLength: maxLength(24)
      }
    },
    initialAvatar: {}
  },

  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: `${this.$t("NAVIGATION.ROOT_MENU.USERS")}`, route: "/users/view" },
      { title: this.calculatedBreadcrumb }
    ]);

    if (this.managementType == "update") {
      this.pageLoader.addRequiredComponent();
      this.fetchExistingUser();
    }
  },

  methods: {
    fetchExistingUser: function() {
      this.$store
        .dispatch(POPULATE_SYSTEM_ADMINISTRATOR_INFO, this.$route.params.id)
        .then(() => {
          for (const [key, value] of Object.entries(this.userInfo)) {
            if (this.form[key] != undefined) {
              this.form[key] = value;
            }
          }
          if (this.userInfo.avatar != null) {
            this.initialAvatar = this.userInfo.project_asset;
          }
          this.form.name = this.userInfo.calculatedName;
          this.form.email = this.userInfo.calculatedEmail;
          this.pageLoader.setCompleteComponent();
        })
        .catch(() => {
          this.error.active = true;
          this.error.message =
            "We're having some issues retrieving the admin roles list, please check back later or contact the helpdesk";
        });
    },
    clearErrors: function() {
      this.$store.dispatch(CLEAR_USER_ERRORS);
    },
    clearData: function() {
      this.$store.dispatch(CLEAR_USER_INFO)
    },
    submit: function() {
      this.clearErrors();

      if (this.managementType == "update") {
        this.submitUpdate();
      } else {
        this.submitPost();
      }
    },
    submitUpdate: function() {
      this.submissionStates.submitting = true;

      let payload = {
        userId: this.$route.params.id,
        data: this.form
      };

      this.$store
        .dispatch(UPDATE_SYS_ADMINISTRATOR, payload)
        .then(() => {
          this.$v.form.$reset();
          this.toast.notify(this, this.form.name + ' ' + `${this.$t("TOASTS.EDIT_SYS_ADMIN.MESSAGE")}`, `${this.$t("TOASTS.EDIT_SYS_ADMIN.TITLE")}`, 'success');
          this.$router.push({
            name: "view_users"
          });
        })
        .catch(() => {})
        .finally(() => {
          this.submissionStates.submitting = false;
        });
    },
    submitPost: function() {
      this.submissionStates.submitting = true;

      this.$store
        .dispatch(CREATE_SYS_ADMINISTRATOR, this.form)
        .then(() => {
          this.$v.form.$reset();
          this.toast.notify(this, this.form.name + ' ' + `${this.$t("TOASTS.ADD_SYS_ADMIN.MESSAGE")}`, `${this.$t("TOASTS.ADD_SYS_ADMIN.TITLE")}`, 'success');
          this.$router.push({
            name: "view_users"
          });
        })
        .catch(() => {})
        .finally(() => {
          this.submissionStates.submitting = false;
        });
    },
    removeAvatar: function(value) {
      this.form.avatar_remove = value;
      this.form.avatar = null;
      this.initialAvatar = null;
    }
  },

  computed: {
    ...mapGetters([
      "systemCountries",
      "systemLanguages",
      "systemTimezones",
      "systemAdminRoles",
      "userInfo",
      "userErrors",
      "currentUser"
    ]),
    assetReservationData: function() {
      let data = {
        scope: 'user',
        data: {
          directory: 'avatars',
          projectId: null
        }
      }

      if (this.managementType == "post") {
        data.data.new = 1;
      } else {
        data.data.userId = this.$route.params.id
      }

      return data;
    },
    calculatedTitle: function() {
      if (this.managementType == "update") {
        return `${this.$t("FORMS.EDIT_SYS_ADMIN.TITLE")}`;
      } else {
        return `${this.$t("FORMS.ADD_SYS_ADMIN.TITLE")}`;
      }
    },
    managementType: function() {
      if (this.$route.params.id != undefined) {
        return "update";
      } else {
        return "post"
      }
    },
    loadingComplete: function() {
      return this.pageLoader.getStatus();
    },
    calculatedStatus: function() {
      if (this.managementType == "update" && this.loadingComplete == true) {
        if (this.userInfo.suspended) {
          return "blocked";
        } else if (this.userInfo.setup_token != null) {
          return "invited";
        } else {
          return "subscribed";
        }
      } else {
        return null;
      }
    },
    tab1Errors: function() {
      if (
        this.$v.form.name.$anyError ||
        this.$v.form.email.$anyError ||
        this.$v.form.suspended.$anyError ||
        this.$v.form.system_country_code.$anyError ||
        this.$v.form.system_language_code.$anyError ||
        this.$v.form.system_timezone_code.$anyError ||
        this.$v.form.system_admin_role_id.$anyError
      ) {
        return true;
      } else {
        return false;
      }
    },
    gallery: function() {
      let gallery = [];

      if (this.managementType == "update") {
        if (this.userInfo.avatar != null) {
          if (this.userInfo.project_asset.id != this.form.avatar_remove) {
            gallery.push(this.userInfo.project_asset);
          }
        }
      }
      
      return gallery;
    },
    calculatedBreadcrumb: function() {
      if (this.managementType == "update") {
        return `${this.$t("NAVIGATION.BREADCRUMB.EDIT_USER")}`
      } else {
        return `${this.$t("NAVIGATION.BREADCRUMB.ADD_USER")}`
      }
    },
    isCurrentUser: function() {
      if (this.managementType == "post") {
        return false;
      } else {
        if (this.currentUser.id == this.$route.params.id) {
          return true;
        } else {
          return false;
        }
      }
    },
    computedvalidation: function() {
      return this.$v.form;
    },
    computedTabs: function() {
      return TopformConfig.topform(this);
    }
  }
};
</script>
